import Separator, {
	SeparatorType,
} from "@components/CustomTypography/Separator/Separator"
import ButtonComponent from "@components/Theme/Button/Button"
import Typography, {
	Variant,
} from "@components/WebsiteRevamp/Typography/Typography"
import {getLink} from "@hooks/PagesData/getLink"
import {CategoryModuleType} from "@hooks/WorkWithJson/GetComponentDataByKey/GetComponentDataByKeyTypes"
import Link from "next/link"
import React, {useEffect, useMemo, useState} from "react"

import ErrorMessage from "../ErrorMessage/ErrorMessage"
import FileInput from "../FileInput/FileInput"
import Input from "../Input/Input"
import TextInput from "../TextInput/TextInput"
import ThankYou from "../ThankYou/ThankYou"
import {
	fileSizeRule,
	maxLengthRule,
	minLengthRule,
	numericOnlyRule,
	prepareErrorResponseForCustomForm,
	requiredRule,
} from "../Validations"

import styles from "./CareersJobForm.module.scss"
import MainBox from "./MainBox"

export type SelectedCategories = {
	[key: string]: string[]
}

const CareersJobForm = (props: {
	container?: SeparatorType
	position: string
	location: string
	categories?: CategoryModuleType[]
}): JSX.Element => {
	const [loading, setLoading] = useState(false)
	const [serverError, setServerError] = useState(false)
	const [firstname, setFirstname] = useState("")
	const [lastname, setLastname] = useState("")
	const [email, setEmail] = useState("")
	const [phone, setPhone] = useState("")
	const [digitalProfile, setDigitalProfile] = useState("")
	const [CV, setCV] = useState<File | null>(null)
	const [coverLetter, setCoverLetter] = useState<File | null>(null)
	const [message, setMessage] = useState("")
	const [formSubmitted, setFormSubmitted] = useState(false)
	const [formSuccess, setFormSuccess] = useState(false)
	const [selectedCategories, setSelectedCategories] =
		useState<SelectedCategories>({})
	const [errors, setErrors] = useState<{
		[key: string]: any
	}>({firstRender: true})

	const handleCheckboxChange = (
		categoryName: string,
		subcategory: string,
	) => {
		setSelectedCategories((prevSelectedCategories: SelectedCategories) => {
			const updatedCategories: SelectedCategories = {
				...prevSelectedCategories,
			}

			if (!updatedCategories[categoryName]) {
				updatedCategories[categoryName] = []
			}

			const subcategories = updatedCategories[categoryName]

			if (subcategories.includes(subcategory)) {
				updatedCategories[categoryName] = subcategories.filter(
					(item) => item !== subcategory,
				)
			} else {
				updatedCategories[categoryName] = [
					...subcategories,
					subcategory,
				]
			}

			return updatedCategories
		})
	}

	// Memoize the MainBox component
	const mainBoxComponent = useMemo(
		() =>
			props.categories && (
				<MainBox
					categories={props.categories}
					handleCheckboxChange={handleCheckboxChange}
					selectedCategories={selectedCategories}
				/>
			),
		[props.categories, handleCheckboxChange, selectedCategories],
	)

	const handleError = (field: string, error: any) => {
		setErrors((prevErrors) => {
			if (
				Object.prototype.hasOwnProperty.call(prevErrors, "firstRender")
			) {
				delete prevErrors.firstRender
			}

			return {
				...prevErrors,
				[field]: error,
			}
		})
	}
	const handleDeleteError = (fieldName: string): void => {
		setErrors((prevErrors) => {
			// Create a new object without the specified property
			const updatedErrors = Object.fromEntries(
				Object.entries(prevErrors).filter(([key]) => key !== fieldName),
			)

			// Check and delete 'firstRender' property
			if (
				Object.prototype.hasOwnProperty.call(
					updatedErrors,
					"firstRender",
				)
			) {
				delete updatedErrors.firstRender
			}

			return updatedErrors
		})
	}

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		setFormSubmitted(!formSubmitted)
	}

	const sendData = async () => {
		try {
			setServerError(false)
			setLoading(true)
			const formData = new FormData()

			formData.append("email", email)
			formData.append("firstname", firstname)
			formData.append("lastname", lastname)
			formData.append("phone", phone)
			formData.append("digitalProfile", digitalProfile)
			formData.append("message", message)

			if (props.position) {
				formData.append("position", props.position)
			}

			if (typeof selectedCategories !== "undefined") {
				formData.append(
					"selectedCategories",
					formatSelectedCategories(selectedCategories),
				)
			}

			if (props.location) {
				formData.append("location", props.location)
			}

			if (CV !== null) {
				formData.append("cv", CV)
			}

			if (coverLetter !== null) {
				formData.append("coverLetter", coverLetter)
			}

			const response = await fetch("/email/send-email", {
				method: "POST",
				body: formData,
			})

			if (!response.ok) {
				const errorData = await response.json()

				const errorsResponse =
					prepareErrorResponseForCustomForm(errorData)

				setErrors(errorsResponse.fields)
				setLoading(false)
			} else {
				setFormSuccess(true)
				setLoading(false)
			}
		} catch (error) {
			setServerError(true)
			setLoading(false)
		}
	}

	useEffect(() => {
		if (Object.keys(errors).length > 0 && formSubmitted) {
			setFormSubmitted(false)
		}
		if (formSubmitted && Object.keys(errors).length <= 0) {
			void sendData()
		}
	}, [formSubmitted])

	const container = {
		className: styles.doubleInput,
	}

	return (
		<Separator withoutMeasure {...props.container}>
			{!formSuccess && (
				<form
					className={`${styles.formContainer} `}
					encType={"multipart/form-data"}
					onSubmit={handleSubmit}
				>
					<Separator
						className={styles.doubleInputWrapper}
						type={"padding"}
						withoutMeasure
					>
						<Input
							container={container}
							disabled={loading}
							errors={errors}
							formSubmitted={formSubmitted}
							label={"First Name "}
							name={"firstname"}
							rules={[
								requiredRule,
								minLengthRule(2),
								maxLengthRule(225),
							]}
							value={firstname}
							onChange={(event) =>
								setFirstname(event.target.value)
							}
							onDeleteError={handleDeleteError}
							onError={handleError}
						/>
						<Input
							container={container}
							disabled={loading}
							errors={errors}
							formSubmitted={formSubmitted}
							label={"Last Name "}
							name={"lastname"}
							rules={[
								requiredRule,
								minLengthRule(2),
								maxLengthRule(225),
							]}
							value={lastname}
							onChange={(event) =>
								setLastname(event.target.value)
							}
							onDeleteError={handleDeleteError}
							onError={handleError}
						/>
					</Separator>
					<Input
						container={container}
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Email "}
						name={"email"}
						rules={[
							requiredRule,
							minLengthRule(2),
							maxLengthRule(225),
						]}
						type={"email"}
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						onDeleteError={handleDeleteError}
						onError={handleError}
					/>

					<Input
						container={container}
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Phone "}
						name={"phone"}
						placeholder={"+358461234567"}
						rules={[
							requiredRule,
							maxLengthRule(225),
							minLengthRule(2),
						]}
						type={"phone"}
						value={phone}
						onChange={(event) => setPhone(event.target.value)}
						onDeleteError={handleDeleteError}
						onError={handleError}
					/>

					{mainBoxComponent}

					<Input
						container={container}
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Digital Profile "}
						name={"digitalProfile"}
						placeholder={"https://dreambroker.com/"}
						rules={[maxLengthRule(1500)]}
						value={digitalProfile}
						onChange={(event) =>
							setDigitalProfile(event.target.value)
						}
						onDeleteError={handleDeleteError}
						onError={handleError}
					/>
					<TextInput
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Message "}
						name={"message"}
						placeholder={
							"Tell us about yourself, your experience and include links to your projects, if you haven't done so yet."
						}
						rules={[maxLengthRule(1500)]}
						value={message}
						onChange={(event) => setMessage(event.target.value)}
						onDeleteError={handleDeleteError}
						onError={handleError}
					/>
					<FileInput
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Resume/CV "}
						name={"cv"}
						rules={[fileSizeRule(2 * 1024 * 1024), requiredRule]}
						value={CV}
						onChange={(event) =>
							setCV(
								typeof event?.target.files?.[0] !== "undefined"
									? event?.target.files?.[0]
									: null,
							)
						}
						onDeleteError={handleDeleteError}
						onError={handleError}
					/>
					<FileInput
						disabled={loading}
						errors={errors}
						formSubmitted={formSubmitted}
						label={"Cover Letter "}
						name={"coverLetter"}
						rules={[fileSizeRule(2 * 1024 * 1024), requiredRule]}
						value={coverLetter}
						onChange={(event) =>
							setCoverLetter(
								typeof event?.target.files?.[0] !== "undefined"
									? event?.target.files?.[0]
									: null,
							)
						}
						onDeleteError={handleDeleteError}
						onError={handleError}
					/>

					<Typography
						className={styles.privacyText}
						variant={Variant.Description}
					>
						By submitting my information I agree to the terms and
						conditions described in the{" "}
						<Link
							href={getLink("privacyPolicy")}
							target="__blank"
							rel={"noreferrer"}
						>
							privacy statement
						</Link>{" "}
						regarding the use of my personal data and the use of
						cookies.
					</Typography>
					<div className={styles.buttonContainer}>
						<ButtonComponent
							action={"submit"}
							buttonType={"primary"}
							className={styles.button}
							disabled={loading}
							size={"large"}
						>
							{!loading ? "Apply now!" : "Applying..."}
						</ButtonComponent>
					</div>
				</form>
			)}
			{serverError && (
				<ErrorMessage
					errors={{
						server: "Something went wrong. Please, try again.",
					}}
					name={"server"}
				/>
			)}
			{formSuccess && (
				<>
					<ThankYou name={firstname} />
				</>
			)}
		</Separator>
	)
}

function formatSelectedCategories(
	selectedCategories: SelectedCategories,
): string {
	const formattedString = Object.entries(selectedCategories)
		.map(([title, categories]) => `${title}: ${categories.join(", ")}`)
		.join(". <br> ")

	return formattedString
}

export default CareersJobForm
